import { Component, OnInit } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AbstractWidgetComponent, ContextChange, MasterDetailListEntry, ResponseMessage, ResponseStatus, TranslationService, UserProfileDialogService, WidgetStyle } from '@sdbn/common';
import { ConfirmDialogComponent, ConfirmDialogData } from '@sdbn/tmc-legacy';
import { Subscription } from 'rxjs';
import { DataProtectionRule, DataProtectionRuleDetails } from '../data-protection-rules-model';
import { DataProtectionRulesService } from '../data-protection-rules.service';

@Component({
  selector: 'app-data-protection-rules',
  templateUrl: './data-protection-rules.component.html',
  styleUrls: ['./data-protection-rules.component.scss']
})
export class DataProtectionRulesComponent extends AbstractWidgetComponent implements OnInit {

  detailsForm: FormGroup;
  formSubscription: Subscription;

  rules: DataProtectionRule[] = [];

  selectedRule: DataProtectionRule;
  ruleDetails: DataProtectionRuleDetails = new DataProtectionRuleDetails();

  responseMessages: ResponseMessage[];
  isLoading = false;

  get cancelEnabled() {
    return !this.isLoading && this.detailsForm.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.detailsForm.dirty && this.detailsForm.valid;
  }

  constructor(
    public translationService: TranslationService,
    private rulesService: DataProtectionRulesService,
    private dialog: MatDialog,
    public userProfileDialogService: UserProfileDialogService) {
    super();

    this.detailsForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      defaultRule: new FormControl('', []),
      disabled: new FormControl('', []),
      phoneEnabled: new FormControl('', []),
      phone: new FormControl('', [Validators.required]),
      numberPlateEnabled: new FormControl('', []),
      numberPlate: new FormControl('', [Validators.required]),
      trailerNumberPlateEnabled: new FormControl('', []),
      trailerNumberPlate: new FormControl('', [Validators.required]),
      swapTrailerNumberEnabled: new FormControl('', []),
      swapTrailerNumber: new FormControl('', [Validators.required]),
      driverEnabled: new FormControl('', []),
      driver: new FormControl('', [Validators.required]),
      coDriverEnabled: new FormControl('', []),
      coDriver: new FormControl('', [Validators.required]),
      carrierEnabled: new FormControl('', []),
      carrier: new FormControl('', [Validators.required]),
      supplierEnabled: new FormControl('', []),
      supplier: new FormControl('', [Validators.required]),
      identityCardEnabled: new FormControl('', []),
      identityCard: new FormControl('', [Validators.required]),
      mailEnabled: new FormControl('', []),
      mail: new FormControl('', [Validators.required]),
      resourcesEnabled: new FormControl('', []),
      resources: new FormControl('', [Validators.required]),
    });
    this.subscribeToForm();
  }

  ngOnInit() {
    this.load();
  }

  setSelected(rule: DataProtectionRule) {

    if (this.selectedRule != null && this.selectedRule.id === rule.id) {
      return;
    }
    const oldSelected = this.selectedRule;

    if (this.ruleDetails != null && this.detailsForm.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.data-protection-rules.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.data-protection-rules.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(rule);
        },
        cancel: () => {
          this.selectedRule = oldSelected;
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(rule);
    }
  }

  setSelectedWithoutCheck(rule: DataProtectionRule) {
    this.isLoading = true;
    this.responseMessages = [];
    if (rule != null) {
      this.rulesService.getDetails(rule.id).subscribe(details => {
        this.selectedRule = rule;
        this.ruleDetails = new DataProtectionRuleDetails(details);
        this.updateDisableFormFieldEnablement() ;
        this.resetForm();
        this.isLoading = false;
      }, error => {
        this.isLoading = false;
        console.log(error);
      });
    } else {
      this.isLoading = false;
    }
  }

  isSelected(details: DataProtectionRule) {
    return this.selectedRule != null && this.selectedRule.id === details.id;
  }

  delete(smartphone: DataProtectionRule) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc-yard.ui.admin.data-protection-rules.delete.confirm-dialog.title',
      messageKey: 'tmc-yard.ui.admin.data-protection-rules.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.rulesService.deleteRule(smartphone.id).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.isLoading = false;
        }, error => {
          console.log(error);
          this.isLoading = false;
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  load() {
    this.isLoading = true;
    this.responseMessages = [];
    this.rulesService.getRules().subscribe(rules => {

      this.rules = [];
      for (const rule of rules) {
        this.rules.push(new DataProtectionRule(rule));
      }
      if (this.rules.length > 0) {
        if (this.selectedRule == null) {
          this.setSelectedWithoutCheck(this.rules[0]);
        } else {
          this.setSelectedWithoutCheck(this.rules.find(s => s.id === this.selectedRule.id));
        }
      }

      this.isLoading = false;
    }, error => {
      console.error(error);
      this.isLoading = false;
    });
  }

  addNew() {
    if (this.ruleDetails != null && this.detailsForm.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.data-protection-rules.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.data-protection-rules.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.ruleDetails != null && this.detailsForm.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc-yard.ui.admin.data-protection-rules.discard.confirm-dialog.title',
        messageKey: 'tmc-yard.ui.admin.data-protection-rules.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    if (this.selectedRule != null && this.detailsForm.dirty) {
      this.setSelectedWithoutCheck(this.selectedRule);
    } else {
      this.ruleDetails = new DataProtectionRuleDetails();
      this.selectedRule = null;
      this.updateDisableFormFieldEnablement() ;
      this.resetForm();
    }
  }

  clear() {
    this.responseMessages = [];
    this.selectedRule = null;
    this.ruleDetails = new DataProtectionRuleDetails();
    this.updateDisableFormFieldEnablement() ;
    this.resetForm();
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  save() {
    if (this.saveEnabled) {
      this.isLoading = true;
      this.responseMessages = [];
      this.rulesService.saveDetails(this.ruleDetails).subscribe(response => {
        this.isLoading = false;
        if (response.status === ResponseStatus.SUCCESS) {
          this.load();
        } else {
          this.responseMessages = response.responseMessages;
        }
      }, error => {
        console.log(error);
        this.isLoading = false;
      });
    }
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as DataProtectionRule);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as DataProtectionRule);
  }

  private subscribeToForm() {
    this.formSubscription = this.detailsForm.valueChanges.subscribe(
      () => {
        if (this.detailsForm.dirty) {
          const values = this.detailsForm.getRawValue();
          this.ruleDetails.name = values.name;
          this.ruleDetails.defaultRule = values.defaultRule;
          this.ruleDetails.disabled = values.disabled;
          this.ruleDetails.phoneEnabled = values.phoneEnabled;
          this.ruleDetails.phone = values.phone;
          this.ruleDetails.numberPlateEnabled = values.numberPlateEnabled;
          this.ruleDetails.numberPlate = values.numberPlate;
          this.ruleDetails.trailerNumberPlateEnabled = values.trailerNumberPlateEnabled;
          this.ruleDetails.trailerNumberPlate = values.trailerNumberPlate;
          this.ruleDetails.swapTrailerNumberEnabled = values.swapTrailerNumberEnabled;
          this.ruleDetails.swapTrailerNumber = values.swapTrailerNumber;
          this.ruleDetails.driverEnabled = values.driverEnabled;
          this.ruleDetails.driver = values.driver;
          this.ruleDetails.coDriverEnabled = values.coDriverEnabled;
          this.ruleDetails.coDriver = values.coDriver;
          this.ruleDetails.carrierEnabled = values.carrierEnabled;
          this.ruleDetails.carrier = values.carrier;
          this.ruleDetails.supplierEnabled = values.supplierEnabled;
          this.ruleDetails.supplier = values.supplier;
          this.ruleDetails.identityCardEnabled = values.identityCardEnabled;
          this.ruleDetails.identityCard = values.identityCard;
          this.ruleDetails.mailEnabled = values.mailEnabled;
          this.ruleDetails.mail = values.mail;
          this.ruleDetails.resourcesEnabled = values.resourcesEnabled;
          this.ruleDetails.resources = values.resources;
          this.updateEnablement();
        }
      }
    );
  }

  private unsubscribeFromForm() {
    this.formSubscription.unsubscribe();
  }

  private resetForm() {

    this.unsubscribeFromForm();

    try {
      const values: any = {};
      values.name = this.ruleDetails.name;
      values.defaultRule = this.ruleDetails.defaultRule;
      values.disabled = this.ruleDetails.disabled;
      values.phoneEnabled = this.ruleDetails.phoneEnabled;
      values.phone = this.ruleDetails.phone;
      values.numberPlateEnabled = this.ruleDetails.numberPlateEnabled;
      values.numberPlate = this.ruleDetails.numberPlate;
      values.trailerNumberPlateEnabled = this.ruleDetails.trailerNumberPlateEnabled;
      values.trailerNumberPlate = this.ruleDetails.trailerNumberPlate;
      values.swapTrailerNumberEnabled = this.ruleDetails.swapTrailerNumberEnabled;
      values.swapTrailerNumber = this.ruleDetails.swapTrailerNumber;
      values.driverEnabled = this.ruleDetails.driverEnabled;
      values.driver = this.ruleDetails.driver;
      values.coDriverEnabled = this.ruleDetails.coDriverEnabled;
      values.coDriver = this.ruleDetails.coDriver;
      values.carrierEnabled = this.ruleDetails.carrierEnabled;
      values.carrier = this.ruleDetails.carrier;
      values.supplierEnabled = this.ruleDetails.supplierEnabled;
      values.supplier = this.ruleDetails.supplier;
      values.identityCardEnabled = this.ruleDetails.identityCardEnabled;
      values.identityCard = this.ruleDetails.identityCard;
      values.mailEnabled = this.ruleDetails.mailEnabled;
      values.mail = this.ruleDetails.mail;
      values.resourcesEnabled = this.ruleDetails.resourcesEnabled;
      values.resources = this.ruleDetails.resources;

      this.detailsForm.reset(values);

      this.updateEnablement();
    } finally {
      this.subscribeToForm();
    }
  }

  private updateEnablement() {
    this.updateControlEnablement('phone');
    this.updateControlEnablement('numberPlate');
    this.updateControlEnablement('trailerNumberPlate');
    this.updateControlEnablement('swapTrailerNumber');
    this.updateControlEnablement('driver');
    this.updateControlEnablement('coDriver');
    this.updateControlEnablement('carrier');
    this.updateControlEnablement('supplier');
    this.updateControlEnablement('identityCard');
    this.updateControlEnablement('mail');
    this.updateControlEnablement('resources');
  }

  private updateControlEnablement(controlName: string) {
    const checkBoxValue = this.detailsForm.controls[controlName + 'Enabled'].value;
    if (checkBoxValue !== true) {
      // onlySelf=true um valueChanges des form zu vermeiden
      this.detailsForm.controls[controlName].disable({ onlySelf: false, emitEvent: false });
    } else {
      // onlySelf=true um valueChanges des form zu vermeiden
      this.detailsForm.controls[controlName].enable({ onlySelf: false, emitEvent: false });
    }
  }

  private updateDisableFormFieldEnablement() {
    if (this.ruleDetails.defaultRule) {
      this.detailsForm.controls.defaultRule.disable({ onlySelf: false, emitEvent: false });
    } else {
      this.detailsForm.controls.defaultRule.enable({ onlySelf: false, emitEvent: false });
    }

  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
